import Select, { selectClasses } from "@mui/joy/Select";
import Tab, { tabClasses } from "@mui/joy/Tab";
import { Box, Button } from "@mui/material";
import { Flex, Switch, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Divider } from "@mui/joy";
import IconButton from "@mui/joy/IconButton";
import Option from "@mui/joy/Option";
import TabList from "@mui/joy/TabList";
import Tabs from "@mui/joy/Tabs";
import Tooltip from "@mui/joy/Tooltip";
import { motion } from "framer-motion";
import { enqueueSnackbar } from "notistack";
import { exampleData } from "../utils/exampleData";
import ExerciseChart from "./ExerciseChart";

const Chart = ({ chartData, weightUnit }) => {
  const [useExampleData, setUseExampleData] = useState(() => {
    const storedValue = localStorage.getItem("useExampleData");
    return storedValue ? JSON.parse(storedValue) : true;
  }); // Set the initial state to true
  const [selectedBodyPart, setSelectedBodyPart] = useState("All");
  const [useOneRM, setUseOneRM] = useState(false);
  const [adjustRPE, setAdjustRPE] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState("All");
  const [currentChart, setCurrentChart] = useState(0);
  const userId = localStorage.getItem("userId");
  const isDemoUser = userId === "3cdf8cc0-6f83-4831-98a1-35b36dcd773d";

  function capitalizeFirstLetters(str) {
    return str.replace(/\b\w/g, (match) => match.toUpperCase());
  }
  useEffect(() => {
    // Define the user ID to check for demo user's data
    const userIdToCheck = "3cdf8cc0-6f83-4831-98a1-35b36dcd773d";

    // Check if the demo user has any data
    const hasUserData = Object.values(chartData).some((array) =>
      array.user_id.includes(userIdToCheck)
    );

    // Determine whether to use example data based on demo user status and data availability
    const shouldUseExampleData = isDemoUser && !hasUserData;

    // Update state and store the useExampleData value in local storage
    setUseExampleData(shouldUseExampleData);
    localStorage.setItem("useExampleData", String(shouldUseExampleData));
  }, [chartData, isDemoUser]);

  const dataToUse = useExampleData ? exampleData : chartData;
  const currentUserData = chartData?.filter((data) => data.user_id === userId);
  const filteredData =
    selectedBodyPart === "All" && selectedExercise === "All"
      ? dataToUse
      : dataToUse.filter((data) => {
          const isBodyPartMatch =
            selectedBodyPart === "All" || data.bodypart === selectedBodyPart;

          const isExerciseMatch =
            selectedExercise === "All" ||
            data.exerciseName === selectedExercise;

          return isBodyPartMatch && isExerciseMatch;
        });

  const finalData = filteredData
    .filter((data) => {
      const isBodyWeight = data.bodypart === "bodyweight";
      const isUserBodyWeight = isBodyWeight && data.user_id === userId;
      const shouldInclude =
        (useExampleData && currentChart === 0 && !isBodyWeight) ||
        (useExampleData && currentChart === 1 && isBodyWeight) ||
        (!useExampleData && currentChart === 1 && isUserBodyWeight) ||
        (!useExampleData && currentChart !== 1 && !isBodyWeight);

      return shouldInclude;
    })
    .map((data) => {
      let adjustedReps = parseFloat(data.reps);
      if (useOneRM && adjustRPE && data.rpe) {
        const rpeAdjustment = 10 - parseFloat(data.rpe);
        adjustedReps += rpeAdjustment;
      }

      const oneRMWeight = (
        data.weight /
        (1.0278 - 0.0278 * adjustedReps)
      ).toFixed(1);

      return {
        ...data,
        weight: useOneRM ? oneRMWeight : data.weight,
        exerciseName: data.exerciseName.toLowerCase(),
        rpe: data.rpe,
      };
    });
  const exerciseNames = Array.from(
    new Set(
      currentUserData
        .filter(
          (data) =>
            data.bodypart === selectedBodyPart ||
            (selectedBodyPart === "All" && data.exerciseName !== "Bodyweight")
        )
        .map((data) => data.exerciseName)
    )
  );
  const bodyParts = Array.from(
    new Set(
      currentUserData
        .map((data) => data.bodypart)
        .filter((data) => data !== "bodyweight")
    )
  );
  console.log("selected bodypart", selectedBodyPart);
  // Add this check for exercise count
  const exerciseCount = exerciseNames.length;
  const shouldShowChart =
    selectedBodyPart !== "All" || exerciseCount <= 20 || currentChart === 1;

  return (
    <>
      <div className="flex flex-col items-center xl:ml-72 mx-auto mt-8 gap-3 overflow-x-hidden">
        <Tabs
          aria-label="tabs"
          defaultValue={0}
          sx={{ bgcolor: "transparent" }}
          onChange={(event, value) => setCurrentChart(value)}
        >
          <TabList
            onClick={() => {
              setSelectedBodyPart("All");
              setSelectedExercise("All");
              setUseOneRM(false);
            }}
            disableUnderline
            sx={{
              p: 0,
              gap: 0.5,
              borderRadius: "xl",
              bgcolor: "background.level1",
              [`& .${tabClasses.root}`]: {
                transition: "box-shadow 0.3s ease, background-color 0.3s ease",
              },
              [`& .${tabClasses.root}[aria-selected="true"]`]: {
                boxShadow: "sm",
                bgcolor: "background.surface",
              },
            }}
          >
            <Tab disableIndicator>Exercise Chart</Tab>
            <Tab
              disableIndicator
              disabled={
                !useExampleData
                  ? !currentUserData?.some(
                      (data) => data.bodypart === "bodyweight"
                    )
                  : false
              }
            >
              Bodyweight Chart
            </Tab>
          </TabList>
        </Tabs>
        {shouldShowChart ? (
          <ExerciseChart
            exerciseData={finalData}
            useOneRM={useOneRM}
            currentChart={currentChart}
            weightUnit={weightUnit}
            adjustRPE={adjustRPE}
          />
        ) : (
          <Text
            color="gray"
            weight="regular"
            className="h-96 flex items-center lg:text-xl sm:text-base"
          >
            Select a body part to view the chart
          </Text>
        )}
        <Flex
          gap="4"
          direction="column"
          style={{
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            width: "100%",
            marginTop: 20,
          }}
        >
          {isDemoUser && (
            <Button
              sx={{ width: "13rem", padding: "4px", fontSize: "13px" }}
              variant="outlined"
              onClick={() => {
                if (currentUserData.length > 0) {
                  setUseExampleData(!useExampleData);
                  setSelectedBodyPart("All");
                } else {
                  setUseExampleData(true);
                  enqueueSnackbar("No Data Available", { variant: "error" });
                }
              }}
            >
              {useExampleData ? "Use Your Data" : "Use Demo Data"}
            </Button>
          )}
          <Flex direction="column" gap="2" className="mx-auto">
            <Flex gap="2" align="center">
              <Text as="label" size="2">
                <Switch
                  className="mr-2"
                  disabled={currentChart === 1 || !shouldShowChart}
                  checked={useOneRM}
                  onCheckedChange={() => {
                    setUseOneRM(!useOneRM);
                    if (!useOneRM) setAdjustRPE(false);
                  }}
                />
                Show estimated 1RM{" "}
              </Text>
              <Tooltip
                leaveTouchDelay={4000}
                arrow
                variant="outlined"
                title="1RM (One Rep Max) is the estimated maximum weight you can lift for a single repetition. It's calculated based on the weight used and reps performed in a set"
              >
                <InfoIcon />
              </Tooltip>
            </Flex>

            {useOneRM && (
              <motion.div
                className="flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Text as="label" size="2">
                  <Switch
                    className="mr-2"
                    disabled={currentChart === 1}
                    checked={adjustRPE}
                    onCheckedChange={() => setAdjustRPE(!adjustRPE)}
                  />
                  Adjust for RPE
                </Text>
                <Tooltip
                  leaveTouchDelay={4000}
                  arrow
                  variant="outlined"
                  title="RPE (Rate of Perceived Exertion) is a 1-10 scale measuring exercise intensity. 1 = could have done 9 more reps, 10 = could have done 0 more reps."
                >
                  <InfoIcon />
                </Tooltip>
              </motion.div>
            )}
          </Flex>
        </Flex>
        <Divider
          sx={{
            width: { md: "10rem", lg: "30rem", sm: "20rem", xs: "20rem" },
            margin: "auto 0",
            alignSelf: "center",
          }}
          orientation="horizontal"
        >
          {" "}
          Filters{" "}
        </Divider>
        <Box
          sx={{
            minWidth: "14.4rem",
          }}
        >
          <Select
            disabled={currentChart === 1}
            placeholder="Filter by body part"
            value={selectedBodyPart}
            indicator={<KeyboardArrowDownIcon />}
            sx={{
              textTransform: "capitalize",
              [`& .${selectClasses.indicator}`]: {
                transition: "0.2s",
                [`&.${selectClasses.expanded}`]: {
                  transform: "rotate(-180deg)",
                },
              },
            }}
          >
            {bodyParts.map((bodyPart) => (
              <Option
                key={bodyPart}
                value={bodyPart}
                sx={{ textTransform: "capitalize" }}
                onClick={() => {
                  setSelectedBodyPart(bodyPart);
                  setSelectedExercise("All");
                }}
              >
                {bodyPart?.charAt(0).toUpperCase() + bodyPart?.slice(1)}
              </Option>
            ))}
          </Select>
        </Box>
        <Box
          sx={{
            minWidth: "14.4rem",
          }}
        >
          <Select
            disabled={currentChart === 1 || !shouldShowChart}
            placeholder="Filter by exercise"
            value={selectedExercise}
            indicator={<KeyboardArrowDownIcon />}
            sx={{
              textTransform: "capitalize",
              [`& .${selectClasses.indicator}`]: {
                transition: "0.2s",
                [`&.${selectClasses.expanded}`]: {
                  transform: "rotate(-180deg)",
                },
              },
            }}
          >
            {exerciseNames
              .filter((exercise) => exercise !== "bodyweight")
              .sort((a, b) => a.localeCompare(b))
              .map((exercise) => (
                <Option
                  key={exercise}
                  value={exercise}
                  sx={{
                    textTransform: "capitalize",
                  }}
                  onClick={() => setSelectedExercise(exercise)}
                >
                  {capitalizeFirstLetters(exercise)}
                </Option>
              ))}
          </Select>
        </Box>
        <Tooltip title="Clear Filters">
          <IconButton
            disabled={currentChart === 1}
            size="sm"
            color="neutral"
            variant="solid"
            onClick={() => {
              setSelectedBodyPart("All");
              setSelectedExercise("All");
            }}
          >
            <FilterListOffIcon />
          </IconButton>
        </Tooltip>
      </div>
    </>
  );
};

export default Chart;
